import type { HealthCopy, HealthGap, HealthSeries, HealthTable } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate, formatNumber } from '@/frontend/features/Fde/dataHealth/types'
import { layerLabel } from '@/frontend/features/Fde/dataHealth/labels'

const DAY = 86_400_000

function day(value?: string | null) {
  if (!value) return null
  const time = Date.parse(`${value.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(time) ? null : time
}

interface Row {
  key: string
  label: string
  sub?: string
  indent: boolean
  first: number | null
  last: number | null
  gaps: HealthGap[]
  daily?: { start: string; counts: number[] } | null
  unreadable?: boolean
}

function rowsOf(tables: HealthTable[], copy: HealthCopy): Row[] {
  return tables.flatMap((table) => [
    {
      key: table.id, label: table.label?.trim() || table.entity, sub: layerLabel(table.layer, copy), indent: false,
      first: day(table.first_date), last: day(table.last_date), gaps: table.gaps ?? [], daily: table.daily_counts,
      unreadable: !table.readable,
    },
    ...(table.partitions ?? []).map((part) => ({
      key: `${table.id}:${part.value}`, label: `${table.partition_column ?? copy.partition} ${part.value}`, indent: true,
      first: day(part.first_date), last: day(part.last_date), gaps: part.gaps ?? [],
    })),
  ])
}

function Sparkline({ counts }: { counts: number[] }) {
  const max = Math.max(1, ...counts)
  const points = counts.map((count, index) => `${(index / Math.max(1, counts.length - 1)) * 100},${100 - (count / max) * 100}`).join(' ')
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
      <polyline points={points} fill="none" vectorEffect="non-scaling-stroke" strokeWidth={1} className="stroke-primary/70" />
    </svg>
  )
}

export default function GapTimeline({ tables, copy }: { tables: HealthTable[]; copy: HealthCopy }) {
  // Tables with daily activity carry the gaps; registers filled now and then would only stretch the axis.
  const dated = tables.filter((table) => table.first_date && table.last_date)
  const daily = dated.filter((table) => table.daily)
  const rows = rowsOf(daily.length ? daily : dated, copy)
  const times = rows.flatMap((row) => [row.first, row.last]).filter((value): value is number => value != null)
  if (!rows.length || !times.length) return null
  const t0 = Math.min(...times)
  const t1 = Math.max(...times) + DAY
  const pct = (time: number) => ((time - t0) / Math.max(DAY, t1 - t0)) * 100

  return (
    <figure className="m-0 grid gap-2 rounded-xl border border-border bg-background px-3 py-3 md:px-4" aria-label={copy.timeline}>
      <div className="grid grid-cols-[minmax(0,8.5rem)_minmax(0,1fr)] gap-x-3 text-[11px] text-muted-foreground md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
        <span />
        <span className="flex justify-between tabular-nums"><span>{formatDate(new Date(t0).toISOString().slice(0, 10))}</span><span>{formatDate(new Date(t1 - DAY).toISOString().slice(0, 10))}</span></span>
      </div>
      {rows.map((row) => {
        const start = row.first
        const end = row.last
        const dailyStart = row.daily ? day(row.daily.start) : null
        return (
          <div key={row.key} className="grid grid-cols-[minmax(0,8.5rem)_minmax(0,1fr)] items-center gap-x-3 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
            <span className={`truncate text-xs ${row.indent ? 'pl-3 text-muted-foreground' : 'font-medium'}`} title={row.label}>
              {row.label}{row.sub && <span className="ml-1 font-normal text-muted-foreground">{row.sub}</span>}
            </span>
            <div className="relative h-4 rounded bg-muted/40">
              {start != null && end != null ? (
                <div
                  className="absolute inset-y-0 overflow-hidden rounded bg-emerald-200/70 dark:bg-emerald-900/60"
                  style={{ left: `${pct(start)}%`, width: `${Math.max(0.6, pct(end + DAY) - pct(start))}%` }}
                  title={`${copy.timeline_with_data}: ${formatDate(new Date(start).toISOString().slice(0, 10))} a ${formatDate(new Date(end).toISOString().slice(0, 10))}`}
                >
                  {row.daily && dailyStart === start && row.daily.counts.length > 1 && <Sparkline counts={row.daily.counts} />}
                </div>
              ) : (
                <span className="absolute inset-0 flex items-center px-2 text-[10px] text-muted-foreground">
                  {row.unreadable ? copy.not_readable : copy.timeline_no_dates}
                </span>
              )}
              {row.gaps.map((gap) => {
                const from = day(gap.from)
                const to = day(gap.to)
                if (from == null || to == null) return null
                return (
                  <div
                    key={`${gap.from}-${gap.to}`}
                    className="absolute inset-y-0 bg-red-500/80 dark:bg-red-500/70"
                    style={{ left: `${pct(from)}%`, width: `${Math.max(0.5, pct(to + DAY) - pct(from))}%` }}
                    title={`${copy.timeline_gap}: ${formatDate(gap.from)} a ${formatDate(gap.to)} (${gap.days})`}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-emerald-300 dark:bg-emerald-800" />{copy.timeline_with_data}</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-red-500" />{copy.timeline_gap}</span>
      </figcaption>
    </figure>
  )
}

export interface DailyMarks {
  // day ranges with no data (gaps, the days after the last data)
  ranges?: { from: string; to: string }[]
  // snapshot tables: a day with zero rows is a day without the snapshot
  zeroIsGap?: boolean
  anomalies?: { date: string; count: number; expected_min: number; expected_max: number }[]
  // one day to point at (the last snapshot) and a reference level (the median)
  focus?: string | null
  reference?: number | null
}

function isoDay(time: number) {
  return new Date(time).toISOString().slice(0, 10)
}

function rowsLabel(copy: HealthCopy, count: number) {
  return format(copy[`rows_${count === 1 ? 'one' : 'other'}`], { count: formatNumber(count) })
}

// Daily volume of one check's period with the measured problem marked on it: days without data, days
// outside the expected range (with that range), the last snapshot against the median.
export function DailySeriesChart({ series, marks = {}, copy }: { series: HealthSeries; marks?: DailyMarks; copy: HealthCopy }) {
  const t0 = day(series.start)
  const counts = series.counts ?? []
  const n = counts.length
  if (t0 == null || !n) return null
  const index = (value: string) => {
    const time = day(value)
    return time == null ? null : Math.round((time - t0) / DAY)
  }
  const anomalies = new Map<number, NonNullable<DailyMarks['anomalies']>[number]>()
  for (const item of marks.anomalies ?? []) {
    const at = index(item.date)
    if (at != null && at >= 0 && at < n) anomalies.set(at, item)
  }
  const ranges = (marks.ranges ?? []).flatMap((range) => {
    const from = index(range.from)
    const to = index(range.to)
    if (from == null || to == null || to < 0 || from >= n) return []
    return [{ from: Math.max(0, from), to: Math.min(n - 1, to) }]
  })
  const focus = marks.focus ? index(marks.focus) : null
  const reference = marks.reference ?? null
  const max = Math.max(1, ...counts, ...[...anomalies.values()].map((item) => item.expected_max), reference ?? 0)
  const y = (value: number) => 100 - (value / max) * 100
  const zeroGap = !!marks.zeroIsGap && counts.some((count) => count === 0)
  const showGap = ranges.length > 0 || zeroGap

  return (
    <figure className="m-0 grid gap-1.5">
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span>{copy.chart_daily_rows}</span>
        <span>{format(copy.chart_max, { value: formatNumber(Math.max(...counts)) })}</span>
      </div>
      <svg viewBox={`0 0 ${n} 100`} preserveAspectRatio="none" className="h-28 w-full border-b border-border" role="img" aria-label={copy.chart_daily_rows}>
        {ranges.map((range) => (
          <rect key={`${range.from}-${range.to}`} x={range.from} y={0} width={range.to - range.from + 1} height={100} className="fill-red-500/15" />
        ))}
        {counts.map((count, at) => {
          const anomaly = anomalies.get(at)
          const height = (count / max) * 100
          const gap = zeroGap && count === 0
          const tone = anomaly ? 'fill-amber-500' : focus === at ? 'fill-red-500' : 'fill-primary/60'
          return (
            <g key={at}>
              <title>{`${formatDate(isoDay(t0 + at * DAY))}: ${rowsLabel(copy, count)}${anomaly ? ` (${format(copy.chart_expected_range, { min: formatNumber(anomaly.expected_min), max: formatNumber(anomaly.expected_max) })})` : ''}`}</title>
              <rect x={at} y={0} width={1} height={100} fill="transparent" />
              {gap && <rect x={at} y={0} width={1} height={100} className="fill-red-500/15" />}
              {anomaly && (
                <rect
                  x={at + 0.1} width={0.8} y={y(anomaly.expected_max)} height={Math.max(0.5, ((anomaly.expected_max - anomaly.expected_min) / max) * 100)}
                  className="fill-amber-500/15 stroke-amber-600" strokeWidth={1} vectorEffect="non-scaling-stroke"
                />
              )}
              {count > 0 && <rect x={at + 0.15} width={0.7} y={100 - Math.max(height, 0.8)} height={Math.max(height, 0.8)} className={tone} />}
            </g>
          )
        })}
        {reference != null && (
          <line x1={0} x2={n} y1={y(reference)} y2={y(reference)} className="stroke-foreground/60" strokeWidth={1} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span>{formatDate(isoDay(t0))}</span>
        <span>{formatDate(isoDay(t0 + (n - 1) * DAY))}</span>
      </div>
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-primary/60" />{copy.chart_rows_per_day}</span>
        {showGap && <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-red-500/30" />{copy.timeline_gap}</span>}
        {anomalies.size > 0 && <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-amber-500" />{copy.chart_anomaly}</span>}
        {anomalies.size > 0 && <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm border border-amber-600 bg-amber-500/15" />{copy.chart_expected}</span>}
        {focus != null && focus >= 0 && focus < n && <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-red-500" />{copy.chart_last_snapshot}</span>}
        {reference != null && <span className="inline-flex items-center gap-1.5"><span className="inline-block h-0 w-3 border-t border-dashed border-foreground/60" />{format(copy.chart_median, { value: formatNumber(reference) })}</span>}
      </figcaption>
    </figure>
  )
}
